// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { bindActionCreatorsSafe } from 'util/safeDispatch';
import RoleRow from './RoleRow';
import { getRoles, getSelectedRole } from 'state/selectors';
import type { Dispatch, Role } from 'state/types';
import {
  createRole as createRoleCommand,
  deleteRole as deleteRoleCommand,
  getRoles as getRolesCommand,
  setSelectedRole as setSelectedRoleCommand,
  updateRole as updateRoleCommand,
} from 'state/commands/roleCommands';
import RoleDetailsForm from './RoleDetailsForm';
import PageHeader from '../PageHeader';
import localizedStrings from 'util/localizedStrings';
import { isRoleCustom } from 'util/roleUtils';
import ModalStringPrompt from 'view/App/DialogPanels/ModalStringPrompt';

type Props = {
  createRoleCommand: typeof createRoleCommand,
  deleteRoleCommand: typeof deleteRoleCommand,
  getRolesCommand: typeof getRolesCommand,
  intl: IntlShape,
  onClick: (SyntheticMouseEvent<>, number) => void,
  roles: Role[],
  selectedRole: ?Role,
  setSelectedRoleCommand: typeof setSelectedRoleCommand,
  updateRoleCommand: typeof updateRoleCommand,
};

type State = {
  isCreateRoleVisible: boolean,
};

class RolesPage extends Component<Props, State> {
  closeCreateRolePrompt: Function;
  handleSelectRow: Function;
  doCreateRole: Function;
  onCreateRole: Function;
  onDeleteRole: Function;
  roleNameValidator: Function;

  state = { isCreateRoleVisible: false };

  constructor() {
    super();
    this.closeCreateRolePrompt = this.closeCreateRolePrompt.bind(this);
    this.doCreateRole = this.doCreateRole.bind(this);
    this.handleSelectRow = this.handleSelectRow.bind(this);
    this.onCreateRole = this.onCreateRole.bind(this);
    this.onDeleteRole = this.onDeleteRole.bind(this);
    this.roleNameValidator = this.roleNameValidator.bind(this);
  }

  componentDidMount() {
    this.getRoles();
  }

  async getRoles() {
    await this.props.getRolesCommand();
    await this.props.setSelectedRoleCommand(this.props.roles[0]);
  }

  handleSelectRow(e: SyntheticMouseEvent<>, index: number) {
    this.props.setSelectedRoleCommand(this.props.roles[index]);
  }

  isSelectedRoleCustom() {
    const role: ?Role = this.props.selectedRole;
    return isRoleCustom(role);
  }

  onCreateRole() {
    this.setState({ isCreateRoleVisible: true });
  }

  closeCreateRolePrompt() {
    this.setState({ isCreateRoleVisible: false });
  }

  roleNameValidator(value) {
    if (!value || !value.length) {
      return 'Empty String';
    } else if (
      this.props.roles
        .map(role => role.name.toLowerCase())
        .includes(value.toLowerCase())
    ) {
      return 'Existing Role';
    }
  }

  doCreateRole(roleName) {
    this.props.createRoleCommand(roleName);
  }

  onDeleteRole() {
    if (this.props.selectedRole) {
      const roleName = this.props.selectedRole.name;
      this.props.deleteRoleCommand(roleName);
    }
  }

  render() {
    const {
      roles,
      intl: { formatMessage },
      selectedRole,
      updateRoleCommand,
    } = this.props;
    return (
      <div className="page" data-test-name="roles-page">
        <div className="page-contents columns is-gapless">
          <div className="column is-one-third page">
            <PageHeader
              deleteDisabled={!this.isSelectedRoleCustom()}
              onClickAdd={this.onCreateRole}
              onClickDelete={this.onDeleteRole}
              title={formatMessage(localizedStrings.NAV_ACCESS_LEVELS)}
            />
            <div className="page bordered-page-column is-scrollable">
              <table
                className="table is-fullwidth is-hoverable"
                data-test-name="role-list"
              >
                <tbody>
                  {roles.map((role, index) => (
                    <RoleRow
                      role={role}
                      index={index}
                      isSelected={
                        !!(selectedRole && role.uuid === selectedRole.uuid)
                      }
                      key={role.uuid}
                      onClick={this.handleSelectRow}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedRole && (
            <RoleDetailsForm
              role={selectedRole}
              updateRole={updateRoleCommand}
            />
          )}
        </div>
        <ModalStringPrompt
          isOpen={this.state.isCreateRoleVisible}
          label={formatMessage(localizedStrings.NAME)}
          onClose={this.closeCreateRolePrompt}
          onSave={this.doCreateRole}
          panelTitle={formatMessage(localizedStrings.ROLES_CREATE_ROLE_TITLE)}
          maxLength={100}
          validate={this.roleNameValidator}
          dataTestName="create-role-modal"
        />
      </div>
    );
  }
}

export default connect(
  state => ({
    roles: getRoles(state),
    selectedRole: getSelectedRole(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      createRoleCommand,
      deleteRoleCommand,
      getRolesCommand,
      setSelectedRoleCommand,
      updateRoleCommand,
    })
)(injectIntl(RolesPage));



// WEBPACK FOOTER //
// ./src/view/Pages/RolesPage/RolesPage.js